import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

/**
 * Editorial masthead: the "Elevare" wordmark traces itself on scroll,
 * behaving like an animated ink drawing on paper. Sits between the
 * Hero and the Editorial Marquee to act as a brand seal / chapter break.
 */
export default function WordmarkTrace() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 90%", "center 55%"],
  });
  const smooth = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });
  // 0 → 1 as the section scrolls into view. We draw from 1 (hidden) → 0 (fully drawn).
  const dashOffset = useTransform(smooth, [0, 1], [1200, 0]);
  const captionOpacity = useTransform(smooth, [0.55, 1], [0, 1]);
  const captionY = useTransform(smooth, [0.55, 1], [10, 0]);

  return (
    <section
      ref={ref}
      data-testid="wordmark-section"
      className="relative bg-[#eae7de] pt-6 pb-14 lg:pt-10 lg:pb-24 overflow-hidden"
    >
      {/* Faint editorial rule */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-4">
          <span className="h-px flex-1 bg-black/10" />
          <span className="text-[10px] uppercase tracking-[0.35em] text-black/45">Masthead</span>
          <span className="h-px flex-1 bg-black/10" />
        </div>
      </div>

      <div className="mt-8 lg:mt-14 relative w-full">
        <svg
          viewBox="0 0 1400 280"
          className="w-full h-auto block"
          preserveAspectRatio="xMidYMid meet"
          aria-label="Elevare wordmark"
        >
          {/* Ghost / paper watermark behind the traced ink */}
          <text
            x="700"
            y="215"
            textAnchor="middle"
            fill="rgba(10,10,10,0.045)"
            style={{
              fontFamily: "Outfit, sans-serif",
              fontSize: 260,
              fontWeight: 300,
              letterSpacing: "-0.055em",
            }}
          >
            Elevare
          </text>

          {/* The traced ink line — dash offset shrinks as user scrolls in */}
          <motion.text
            x="700"
            y="215"
            textAnchor="middle"
            fill="none"
            stroke="#1e3d3a"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              fontFamily: "Outfit, sans-serif",
              fontSize: 260,
              fontWeight: 300,
              letterSpacing: "-0.055em",
              strokeDasharray: 1200,
              strokeDashoffset: dashOffset,
            }}
          >
            Elevare
          </motion.text>

          {/* Gold underline swoosh — echoes the brand card wave */}
          <motion.path
            d="M 300 250 Q 700 275 1100 250"
            fill="none"
            stroke="#c9a961"
            strokeWidth="2"
            strokeLinecap="round"
            style={{
              pathLength: useTransform(smooth, [0.15, 0.95], [0, 1]),
            }}
          />

          {/* SOLUTIONS in gold caps — matches the brand card */}
          <motion.text
            x="700"
            y="265"
            textAnchor="middle"
            fill="#c9a961"
            style={{
              fontFamily: "Outfit, sans-serif",
              fontSize: 22,
              fontWeight: 500,
              letterSpacing: "0.8em",
              opacity: captionOpacity,
            }}
          >
            SOLUTIONS
          </motion.text>
        </svg>

        {/* Editorial caption */}
        <motion.div
          style={{ opacity: captionOpacity, y: captionY }}
          className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-2 lg:mt-4 flex items-center justify-between gap-6"
        >
          <span className="text-[11px] uppercase tracking-[0.28em] text-black/55">Vol. 01</span>
          <p className="text-sm lg:text-base text-black/60 italic text-center max-w-md">
            <span className="not-italic font-medium text-[#1e3d3a]">Elevare</span> — Latin,
            &ldquo;to elevate, to lift beyond the surface.&rdquo;
          </p>
          <span className="text-[11px] uppercase tracking-[0.28em] text-black/55">A field guide</span>
        </motion.div>
      </div>
    </section>
  );
}
